import React, { useState } from 'react'
import { Image, ImageBackground, Platform } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import {
  B4,
  B5,
  Button,
  H6,
  Icon,
  SH3,
  SH4,
  Stack,
  XStack,
  YStack,
  colors,
} from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import Pressable from '@zet/design-system/pressable'
import { APP_NAME } from '@zet/end-points'
import { useExperiments } from '@zet/experiments'
import { sendAnalyticsData } from '@zet/integration-flow/utils/analyticsEvents'
import { i18n } from '@zet/localization'
import InterestRateIcon from '@zet/svg/loanBrandCard/interestRate.svg'
import MoneyBag from '@zet/svg/loanBrandCard/moneyBag.svg'
import OfferTag from '@zet/svg/offerTag.svg'
import { Categories } from '@zet/utils/Constants'
import { LinearGradient } from 'expo-linear-gradient'
import { useUnsafeVariable } from 'nativewind'
import { SvgUri } from 'react-native-svg'

import { LoyaltyTierDetails } from '../../api/brands/types'
import { useSellBrand } from '../../hooks/useSellBrand'
import useVariantListingStore from '../../hooks/useVariantListingStore'
import { magnetIds } from '../../utils/constants'
import BrandDetailsBottomSheet from '../brandDetailsBottomSheet'
import ExploreOtherEligibleCardBS from '../exploreOtherEligibleCardBS'
import WebApplyButton from '../productCard/webApplyButton'
import BrandCardEligiblity from './brandCardEligiblity'
import BrandCardTag from './brandCardTag'
import FeeHighlight from './feeHighlight'
import PayoutChip from './payoutChip'

export type BrandCardProps = {
  isOnHold?: boolean
  shimmerData?: {
    id: number
    createdAt: string
    topBarColors: string
    liveOfferCode: string
    liveOfferText: string
    topBarText: string
    productId: string
    topBarIcon: {
      id: number
      url: string
    }
    liveOfferStrip: {
      id: number
      url: string
    }
  }
  nudgeText?: string
  preapprovedPartner?: boolean
  isLongJourney?: boolean
  variantId: number
  id: number
  name?: string
  partnerId: number
  groupCode: number
  groupName: string
  variantName: string
  partnerName: string
  imageUrl: string
  remainingCount?: number
  isNewBrand?: boolean
  eligibility: {
    isEligible: true
    reason: ''
    criteria: ''
    eligible: true
  }
  variantApprovalDetails: {
    approvalRateTextColor: 'HIGH'
    approvalRateText: 'More than 60%'
    highApproval: true
  }
  backgroundGradient: string[]
  rejectionPayout?: string
  payout?: {
    amount: number
    type: 'flat' | 'percentage'
  }
  benefits?: {
    tag?: string | number
    icon: string
    heading?: string
    subText?: string[]
  }[]
  joiningFees?: string
  renewalFees?: string
  voucher?: string
  isSpendBasedWaiver?: string
  leadExists?: boolean
  fdAmount?: string
}

const isWeb = Platform.OS === 'web'

const generateSnapPoints = (
  _minHeight: string | number,
  _maxHeight: string | number,
) => [Number(_maxHeight) * 0.89]

const getIcon = (type?: string) => {
  switch (type) {
    case 'bag':
      return <MoneyBag />
    case 'interestRate':
      return <InterestRateIcon />
    default:
      return <OfferTag />
  }
}

const BrandCardV2 = ({
  data,
  tierData,
  showPayout,
  categoryId,
  currentTier,
  isHeroCard = false,
  isSourceWeb,
  eligibleCardsCountExcludingMagnet,
  isJfsFlow,
}: {
  showPayout?: boolean
  data: BrandCardProps
  categoryId: number
  tierData: LoyaltyTierDetails
  currentTier: object
  isHeroCard: boolean
  isSourceWeb?: boolean
  eligibleCardsCountExcludingMagnet?: number
  disabled?: boolean
  isJfsFlow?: boolean
}) => {
  const { navigate } = useNavigation()
  const { params } = useRoute()
  const subFilterRef = useVariantListingStore((state) => state?.subFilterRef)
  const [colorPrimary100] = useUnsafeVariable('--color-primary-100')
  const [colorPrimary150] = useUnsafeVariable('--color-primary-150')

  const { onPress, isEligibleForSale } = useSellBrand({
    brandId: data?.partnerId,
    isEligibleBrand: data?.eligibility?.isEligible,
    isOnHold: data?.isOnHold,
    customerId: params?.customerId,
    variantId: data?.id,
    criteria: data?.eligibility?.criteria,
    categoryId,
    leadSource: data?.isLongJourney ? 'plWeb' : null,
  })

  const liveOfferStripAvailable = Boolean(
    data?.shimmerData?.liveOfferStrip?.url,
  )

  const showLiveOfferStrip = Boolean(
    data?.shimmerData?.liveOfferStrip?.url ||
      (data?.shimmerData?.liveOfferText && data?.shimmerData?.liveOfferCode),
  )

  const [isBottomSheetVisible, setIsBottomSheetVisible] =
    useState<boolean>(false)

  const [isExploreOtherCardsBSVisible, setIsExploreOtherCardsBSVisible] =
    useState(false)

  const { data: experiments } = useExperiments()
  const isRecommendationFlow = Boolean(params?.customerId)
  const isMyTeamsAdvisor = Boolean(APP_NAME === MY_TEAMS_ADVISOR)

  const onProductCardPress = () => {
    publishEvent('click_action', {
      item_name: `brand_${
        isWeb
          ? 'recommendation_web'
          : params?.customerId
            ? 'recommendation_app'
            : 'card'
      }`,
      brand: data?.variantName || data?.name,
    })
    if (isWeb) {
      setIsBottomSheetVisible(true)
      return
    }

    navigate('BrandDetails', {
      isEligible: true,
      brandDetails: true,
      brandId: data?.partnerId,
      variantId: data?.id,
      routeName: 'category',
      customerId: params?.customerId,
      isPreApprovedPartner: data?.preapprovedPartner,
      // Cards tapped on the rejection view attribute the lead to the
      // rejection flow (BrandDetails forwards this into the share flow).
      ...(params?.rejectedBrandName
        ? { leadSource: 'reject-page-app' }
        : {}),
    })
  }

  const showTopTag =
    data?.isOnHold ||
    data?.preapprovedPartner ||
    (data?.shimmerData?.topBarColors && data?.shimmerData?.topBarText)

  const snapPoints = isWeb ? generateSnapPoints : undefined

  const variantApprovalDetails =
    data?.partnerId === magnetIds.brandId
      ? {
          approvalRateTextColor: 'HIGH',
          approvalRateText: '100%',
          highApproval: true,
        }
      : (data?.variantApprovalDetails ?? null)

  return (
    <>
      <Pressable className="items-center" onPress={onProductCardPress}>
        <Stack
          className={` w-full overflow-hidden ${
            showTopTag ? (isJfsFlow ? 'rounded-t-2xl' : 'rounded-t-lg') : ''
          } ${isHeroCard ? 'rounded-b-[16px] rounded-t-[16px]' : ''}`}
        >
          {showTopTag && (
            <BrandCardTag
              tagType={
                data?.isOnHold
                  ? 'onHold'
                  : data?.preapprovedPartner
                    ? 'preApproved'
                    : undefined
              }
              topBarText={data?.shimmerData?.topBarText}
              topBarColors={data?.shimmerData?.topBarColors}
              topBarIcon={data?.shimmerData?.topBarIcon?.url}
            />
          )}

          <LinearGradient
            colors={
              isJfsFlow
                ? ['#fff', '#fff']
                : [
                    data?.backgroundGradient?.[0] ?? '#B6D7E9',
                    data?.backgroundGradient?.[1] ?? '#FFFFFF',
                  ]
            }
            start={{ x: 0, y: 0 }}
            end={{ x: 0.5, y: 0.5 }}
            locations={isWeb ? [0.1, 0.4] : [0.25, 0.75]}
            className={`border-grays-40 w-full ${
              isJfsFlow ? 'rounded-b-2xl' : 'rounded-b-lg'
            } border ${
              showTopTag
                ? 'border-t-0'
                : `${isJfsFlow ? 'rounded-t-2xl' : 'rounded-t-lg'}`
            }`}
          >
            <Stack className={`pb-4/2 px-4/2 ${isJfsFlow ? 'pt-2' : 'pt-3'}`}>
              <XStack
                className="flex-auto justify-between"
                style={{
                  height:
                    categoryId === Categories.CREDIT_CARDS ||
                    data?.partnerId === magnetIds.brandId
                      ? 100
                      : 80,
                }}
              >
                <Stack
                  className={`${
                    variantApprovalDetails?.approvalRateTextColor &&
                    (categoryId === Categories.CREDIT_CARDS ||
                      data?.partnerId === magnetIds.brandId)
                      ? 'justify-start'
                      : 'justify-center'
                  }`}
                  style={{
                    flex: 0.6,
                  }}
                >
                  {data?.groupName ? (
                    <SH3 className="text-grays-100" numberOfLines={1}>
                      {data?.groupName}
                    </SH3>
                  ) : null}
                  <H6 className="text-grays-100" numberOfLines={1}>
                    {data?.variantName || data?.name || ''}
                  </H6>
                  {/* Approval and Eligiblity Section*/}
                  {Boolean(variantApprovalDetails?.approvalRateTextColor) && (
                    <BrandCardEligiblity
                      approvalRate={variantApprovalDetails}
                    />
                  )}
                </Stack>
                <Stack
                  style={{
                    width: '33%',
                    flex: 0.35,
                  }}
                >
                  <Image
                    className="h-full w-full flex-1 rounded-md"
                    source={{
                      uri: data?.imageUrl,
                    }}
                    resizeMode="contain"
                  />
                </Stack>
              </XStack>

              {(data?.joiningFees || data?.renewalFees || data?.fdAmount) && (
                <XStack style={{ columnGap: 12 }} className="mt-4/2">
                  {(data?.fdAmount || data?.joiningFees) && (
                    <>
                      {data?.fdAmount ? (
                        <FeeHighlight
                          title={i18n.t('brandCard.fdStarts')}
                          titlePostfix=""
                          amount={data.fdAmount}
                        />
                      ) : (
                        data?.joiningFees && (
                          <FeeHighlight
                            title={i18n.t('brandCard.joiningFee')}
                            titlePostfix={data?.isSpendBasedWaiver ? '*' : ''}
                            amount={data.joiningFees}
                          />
                        )
                      )}
                    </>
                  )}
                  {data?.renewalFees && (
                    <FeeHighlight
                      title={i18n.t('brandCard.renewalFee')}
                      amount={data?.renewalFees}
                      isJfsFlow={isJfsFlow}
                    />
                  )}
                </XStack>
              )}
              {Boolean(data?.isSpendBasedWaiver) && (
                <XStack className="mt-2">
                  <B5 className="text-grays-70">
                    *{i18n.t('brandCard.spendBasedWaiver')}
                  </B5>
                </XStack>
              )}
              {/* Benefits Section*/}
              {Boolean(data?.benefits?.length) && (
                <YStack className="gap-y-2 pt-3">
                  {data?.benefits?.map((benefit, index) => {
                    return (
                      <XStack className="items-center" key={index}>
                        {benefit?.icon?.includes('https://') ? (
                          isWeb ? (
                            <Image
                              source={{ uri: benefit?.icon }}
                              className="h-[20px] w-[20px] object-contain"
                            />
                          ) : (
                            <SvgUri
                              uri={benefit?.icon}
                              width={20}
                              height={20}
                            />
                          )
                        ) : (
                          getIcon(benefit?.icon)
                        )}
                        {benefit?.subText?.map((text) => {
                          return (
                            <B4
                              className="text-grays-100 ml-1 flex-1"
                              key={text}
                            >
                              {text}
                            </B4>
                          )
                        })}
                      </XStack>
                    )
                  })}
                </YStack>
              )}

              <XStack className="mb-2 mt-3 items-center">
                <B4
                  className={`${
                    isJfsFlow ? 'text-primary-150' : 'text-primary-100'
                  }`}
                >
                  {i18n.t('brandCard.viewAllBenefits')}
                </B4>
                <Icon
                  type="ArrowRight"
                  size={20}
                  color={isJfsFlow ? colorPrimary150 : colorPrimary100}
                />
              </XStack>
              {/* Ends Benefits Section*/}

              {/* Starts Offer Code Section */}

              {showLiveOfferStrip && (
                <Stack className="mb-[-2] mt-2 h-[35] w-[100%] justify-center">
                  <ImageBackground
                    source={
                      liveOfferStripAvailable
                        ? { uri: data?.shimmerData?.liveOfferStrip?.url }
                        : require('../../assets/images/couponBackground.png')
                    }
                    style={{
                      width: '100%',
                      height: 40,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    resizeMode="stretch"
                  >
                    {!liveOfferStripAvailable && (
                      <Stack
                        className={`w-[90%] items-center justify-center ${
                          String(data?.shimmerData?.liveOfferText)?.length <= 35
                            ? 'flex-row'
                            : 'flex-col'
                        }`}
                      >
                        <B4 className="mr-1 text-center font-normal text-[#412D09]">
                          {data?.shimmerData?.liveOfferText}
                        </B4>
                        <SH4 className="text-center font-[Graphie-Bold] text-[#412D09]">
                          {data?.shimmerData?.liveOfferCode}
                        </SH4>
                      </Stack>
                    )}
                  </ImageBackground>
                </Stack>
              )}

              {/* Ends Offer Code Section */}

              {!isJfsFlow &&
                isWeb &&
                data?.eligibility?.isEligible &&
                params?.customerId && (
                  <Stack
                    className={`${isJfsFlow ? '' : 'mb-2'} ${
                      showLiveOfferStrip
                        ? isJfsFlow
                          ? 'mt-3'
                          : 'mt-4'
                        : 'mt-2'
                    }`}
                  >
                    <Alert intent={'success'}>
                      <B5 className={'text-success-150'} numberOfLines={2}>
                        {i18n.t('brandCard.eligibleAlert', {
                          name: data?.variantName || data?.name,
                        })}
                      </B5>
                    </Alert>
                  </Stack>
                )}
              {/*  Payout Section */}
              {!isWeb && !!data?.rejectionPayout && showPayout && (
                <XStack className="border-grays-50 mt-2 items-center gap-x-1 border border-l-0 border-r-0 border-dashed py-2">
                  <Icon
                    type="RupeeCircleOutlined"
                    size={20}
                    color={colors.error[100]}
                  />
                  <SH3 className="text-error-200" numberOfLines={3}>
                    {data?.rejectionPayout}
                  </SH3>
                </XStack>
              )}
              {/* Ends Payout Section */}
              {/*  InEligiblity Section */}
              {!data?.preapprovedPartner && !data?.eligibility?.isEligible && (
                <XStack
                  className={`bg-error-10 ${
                    showLiveOfferStrip ? 'mt-4' : 'mt-2'
                  } items-center space-x-2 rounded px-2 py-2`}
                >
                  <Icon
                    type="CautionOutlined"
                    size={20}
                    color={colors.error[150]}
                  />
                  <B5 className="text-error-150" numberOfLines={3}>
                    {data?.eligibility?.reason ||
                      i18n.t('recommendationCard.notEligible', {
                        defaultValue: '',
                      })}
                  </B5>
                </XStack>
              )}
              {/* Ends InEligiblity Section */}
            </Stack>
            <Stack
              className={`border-grays-40 p-4/2 ${
                !isJfsFlow &&
                isWeb &&
                isEligibleForSale &&
                data?.eligibility?.isEligible
                  ? 'border-t'
                  : ''
              } pt-0`}
            >
              {/* Sell Section*/}
              {isWeb ? (
                isEligibleForSale ? (
                  <WebApplyButton
                    partnerId={data?.partnerId as string}
                    alreadyApplied={data?.leadExists}
                    isSourceWeb={isSourceWeb}
                    isInEligible={!data?.eligibility?.isEligible}
                    customerId={params?.customerId}
                    customerPhoneNumber={params?.customerPhoneNumber}
                    leadAdded={data?.leadExists}
                    customerName={params?.customerName}
                    eligibleCardsCountExcludingMagnet={
                      eligibleCardsCountExcludingMagnet
                    }
                    setIsExploreOtherCardsBSVisible={
                      setIsExploreOtherCardsBSVisible
                    }
                    variantId={data?.id}
                  />
                ) : (
                  <></>
                )
              ) : (
                <XStack className="mt-2 items-center justify-between">
                  {!isMyTeamsAdvisor && data?.payout && showPayout && (
                    <YStack>
                      <B4 className="text-grays-70">
                        {i18n.t('brandCard.payout')}
                      </B4>

                      <PayoutChip
                        brandName={data?.variantName || data?.name || ''}
                        currentTier={currentTier}
                        isUserCurrentTierPayout
                        categoryId={categoryId}
                        partnerId={data?.partnerId}
                        partnerCategoryId={data?.partnerCategoryId}
                        isRecommendationFlow={isRecommendationFlow}
                        isHighestLoyaltyPayout={
                          experiments?.UI_HighestPayoutFlow &&
                          !isRecommendationFlow
                        }
                        isAllLevelPayout={false}
                        tierData={tierData}
                        payout={data?.payout}
                      />
                    </YStack>
                  )}
                  {(isEligibleForSale || data?.preapprovedPartner) && (
                    <Button
                      fullWidth={!data?.payout || !showPayout}
                      onPress={() => {
                        publishEvent('click_action', {
                          item_name: `sell_now_new_${
                            params?.customerId
                              ? 'recommendation_app'
                              : 'brand_list'
                          }`,
                        })
                        onPress()
                      }}
                      size={'medium'}
                      className={`justify-center items-center
                        ${data?.payout?.amount == null ? 'flex-1' : ''}
                        ${data?.eligibility?.isEligible ? '' : "w-['40%'] px-0"}
                        ${
                          !(
                            data?.eligibility?.isEligible ||
                            data?.preapprovedPartner
                          )
                            ? 'bg-primary-10'
                            : ''
                        }`}
                      textStyle={`${
                        !(
                          data?.eligibility?.isEligible ||
                          data?.preapprovedPartner
                        )
                          ? 'text-primary-100'
                          : ''
                      }`}
                    >
                      {data?.eligibility?.isEligible ||
                      data?.preapprovedPartner
                        ? i18n.t('shareCardLink.sellNow')
                        : i18n.t('recommendationBottomSheet.sellAnywayCTA')}
                    </Button>
                  )}
                </XStack>
              )}
            </Stack>
          </LinearGradient>
        </Stack>

        {!!data?.remainingCount && (
          <Pressable
            hitSlop={{ top: 5, bottom: 10, left: 10, right: 10 }}
            className="w-full items-center"
            onPress={() => {
              publishEvent('click_action', {
                item_name: `view_other_credit_cards_${
                  isWeb
                    ? 'recommendation_web'
                    : params?.customerId
                      ? 'recommendation_app'
                      : 'brands'
                }`,
                brand: data?.groupName || data?.variantName || data?.name,
              })
              if (isJfsFlow) {
                sendAnalyticsData({
                  screen: 'FILTERSCREEN_SCREEN_CTA',
                  type: 'click',
                  optional_data: {
                    brand: data?.variantName || data?.name || data?.groupName,
                    type: 'view remaining cards',
                  },
                })
              }
              subFilterRef?.current?.updateLcalState({
                filterState: 'groupCode',
                subFIlterState: {
                  groupCode: [data?.groupCode?.toString()],
                },
                shouldTriggerSubmit: true,
              })
            }}
          >
            <XStack
              style={{ width: '90%' }}
              className="border-grays-40 items-center justify-center rounded-b-lg border border-t-0 bg-white px-3 py-2"
            >
              <B4 className="text-primary-100" numberOfLines={1}>
                {i18n.t('brandCard.remainingCount', {
                  count: data?.remainingCount,
                  groupName: data?.groupName,
                })}
              </B4>
              <Icon type="ArrowDown" size={20} color={colorPrimary100} />
            </XStack>
          </Pressable>
        )}
      </Pressable>

      <BrandDetailsBottomSheet
        variantName={data?.variantName || data?.name}
        snapPoints={snapPoints}
        isBottomSheetVisible={isBottomSheetVisible}
        setIsBottomSheetVisible={setIsBottomSheetVisible}
        navigate={navigate}
        brandId={data?.partnerId}
        isEligible={data?.eligibility?.isEligible}
        showShareButton={true}
        viewBrandDetailsButton={false}
        alreadyApplied={data?.leadExists}
        isSourceWeb={isSourceWeb}
        isInEligible={!data?.eligibility?.isEligible as boolean}
        customerId={params?.customerId}
        // index={index}
        customerPhoneNumber={params?.customerPhoneNumber}
        variantId={data?.id}
        leadAdded={data?.leadExists}
        customerName={params?.customerName}
        setIsExploreOtherCardsBSVisible={setIsExploreOtherCardsBSVisible}
        eligibleCardsCountExcludingMagnet={eligibleCardsCountExcludingMagnet}
      />

      {isWeb && (
        <ExploreOtherEligibleCardBS
          snapPoints={isWeb ? (minHeight: number) => [minHeight] : undefined}
          isVisible={isExploreOtherCardsBSVisible}
          setIsVisible={setIsExploreOtherCardsBSVisible}
          approvalRate={variantApprovalDetails}
          eligibleCardsCountExcludingMagnet={eligibleCardsCountExcludingMagnet}
          setIsBrandDetailsBSVisible={setIsBottomSheetVisible}
        />
      )}
    </>
  )
}

export default React.memo(BrandCardV2)
